import { useState, useEffect } from 'react';
import Select from 'react-select';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faBolt, faFire, faEye, faEdit, faTrash, faPlus, faFilter, faSearch, faCalendarAlt, faStar
} from '@fortawesome/free-solid-svg-icons';
import AddLimitedOfferModal from '../components/modals/AddLimitedOfferModal';
import ViewLimitedOfferModal from '../components/modals/ViewLimitedOfferModal';
import EditLimitedOfferModal from '../components/modals/EditLimitedOfferModal';
import DeleteConfirmationModal from '../components/modals/DeleteConfirmationModal';
import { GetLimitedOffers, DeleteLimitedOffer } from '../APIS/apis/LimitedOfferApi';
import { useDispatch, useSelector } from 'react-redux';
import { toast } from "react-toastify";

export default function LimitedOffers() {
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedStatus, setSelectedStatus] = useState(null);
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [viewModal, setViewModal] = useState({ isOpen: false, offer: null });
  const [editModal, setEditModal] = useState({ isOpen: false, offer: null });
  const [deleteModal, setDeleteModal] = useState({ isOpen: false, offer: null });
  const [isDeleting, setIsDeleting] = useState(false);
  const dispatch = useDispatch();

  useEffect(() => {
    dispatch(GetLimitedOffers());
  }, [dispatch]);

  const { limitedOfferData } = useSelector((state) => state?.LimitedOfferSlice);

  // Map API data to display format
  const offers = limitedOfferData?.map((offer, index) => ({
    _id: offer._id, // Keep the MongoDB _id
    id: offer._id,
    serialNumber: index + 1,
    title: offer.title,
    description: offer.description,
    product: offer.product, // Keep the full product object/ID
    originalPrice: offer.originalPrice,
    offerPrice: offer.offerPrice,
    discount: offer.discount,
    startDate: offer.startDate,
    endDate: offer.endDate,
    status: offer.status.charAt(0).toUpperCase() + offer.status.slice(1),
    stockLimit: offer.stockLimit,
    soldCount: offer.soldCount,
    featured: offer.featured
  })) || [];

  // Calculate stats
  const totalOffers = offers.length;
  const activeOffers = offers.filter(o => o.status === 'Active').length;
  const expiredOffers = offers.filter(o => o.status === 'Expired').length;
  const scheduledOffers = offers.filter(o => o.status === 'Scheduled').length;

  // Handle view
  const handleViewClick = (offer) => {
    setViewModal({ isOpen: true, offer });
  };

  // Handle edit
  const handleEditClick = (offer) => {
    setEditModal({ isOpen: true, offer });
  };

  // Handle delete
  const handleDeleteClick = (offer) => {
    setDeleteModal({ isOpen: true, offer });
  };

  const handleDeleteConfirm = async () => {
    setIsDeleting(true);
    try {
      const res = await dispatch(DeleteLimitedOffer(deleteModal.offer.id));
      if (res?.payload?.success === true) {
        toast.success('Limited offer deleted successfully!');
        dispatch(GetLimitedOffers());
      } else {
        toast.error(res?.payload?.message || 'Failed to delete offer');
      }
    } catch (error) {
      console.error('Error deleting offer:', error);
      toast.error('Something went wrong. Please try again.');
    } finally {
      setIsDeleting(false);
      setDeleteModal({ isOpen: false, offer: null });
    }
  };

  // Status options
  const statusOptions = [
    { value: 'all', label: 'All Status' },
    { value: 'active', label: 'Active' },
    { value: 'scheduled', label: 'Scheduled' },
    { value: 'expired', label: 'Expired' }
  ];

  // Custom styles for react-select
  const customSelectStyles = {
    control: (base, state) => ({
      ...base,
      borderRadius: '8px',
      border: '1px solid var(--sand-300)',
      padding: '0.25rem',
      boxShadow: state.isFocused ? '0 0 0 3px rgba(179, 135, 63, 0.1)' : 'none',
      borderColor: state.isFocused ? 'var(--sand-600)' : 'var(--sand-300)',
      '&:hover': {
        borderColor: 'var(--sand-600)'
      }
    }),
    option: (base, state) => ({
      ...base,
      backgroundColor: state.isSelected ? 'var(--sand-600)' : state.isFocused ? 'var(--sand-200)' : 'white',
      color: state.isSelected ? 'white' : 'var(--sand-900)',
      cursor: 'pointer',
      padding: '0.5rem 1rem'
    }),
    menu: (base) => ({
      ...base,
      borderRadius: '8px',
      overflow: 'hidden',
      boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
      zIndex: 9999
    })
  };

  // Filter offers
  const filteredOffers = offers.filter(offer => {
    const statusMatch = !selectedStatus || selectedStatus.value === 'all' || offer.status.toLowerCase() === selectedStatus.value;
    const searchMatch = !searchQuery || 
      offer.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
      offer.description.toLowerCase().includes(searchQuery.toLowerCase());
    return statusMatch && searchMatch;
  });

  // Get status color
  const getStatusColor = (status) => {
    switch (status) {
      case 'Active': return '#28a745';
      case 'Scheduled': return '#ffc107';
      case 'Expired': return '#dc3545';
      default: return '#6c757d';
    }
  };

  return (
    <div>
      {/* Page Header */}
      <div className="mb-4">
        <h2 className="mb-2" style={{
          color: 'var(--sand-900)',
          fontSize: '1.5rem',
          fontWeight: '600'
        }}>
          Limited Time Offers
        </h2>
        <p className="mb-0" style={{ 
          color: 'var(--sand-700)', 
          fontSize: '0.875rem'
        }}>
          Create and manage exclusive flash deals and limited offers
        </p>
      </div>

      {/* Stats Cards */}
      <div className="row g-3 g-md-4 mb-4">
        {/* Total Offers */}
        <div className="col-6 col-lg-3">
          <div className="h-100" style={{
            background: 'linear-gradient(135deg, rgba(255, 87, 34, 0.1) 0%, rgba(244, 67, 54, 0.1) 100%)',
            borderRadius: '16px',
            padding: 'clamp(1rem, 3vw, 1.8rem)',
            transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
            cursor: 'pointer',
            border: '1px solid rgba(255, 255, 255, 0.8)',
            boxShadow: '0 4px 15px rgba(0, 0, 0, 0.08)',
            position: 'relative',
            overflow: 'hidden'
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.transform = 'translateY(-8px) scale(1.02)';
            e.currentTarget.style.boxShadow = '0 12px 35px rgba(0, 0, 0, 0.15)';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.transform = 'translateY(0) scale(1)';
            e.currentTarget.style.boxShadow = '0 4px 15px rgba(0, 0, 0, 0.08)';
          }}>
            <div style={{
              position: 'absolute',
              top: '-20px',
              right: '-20px',
              width: '80px',
              height: '80px',
              borderRadius: '50%',
              background: 'linear-gradient(135deg, #ff5722 0%, #f44336 100%)',
              opacity: 0.1,
              animation: 'pulse 3s ease-in-out infinite'
            }} />
            
            <div className="d-flex justify-content-between align-items-start mb-3 position-relative">
              <div className="flex-grow-1">
                <p className="mb-2 text-uppercase" style={{
                  fontSize: 'clamp(0.7rem, 1.5vw, 0.9rem)',
                  color: 'var(--sand-700)',
                  fontWeight: '600',
                  letterSpacing: '0.5px'
                }}>
                  Total Offers
                </p>
                <h3 className="mb-0" style={{
                  fontSize: 'clamp(1.5rem, 4vw, 2.2rem)',
                  fontWeight: '800',
                  background: 'linear-gradient(135deg, #ff5722 0%, #f44336 100%)',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  backgroundClip: 'text'
                }}>
                  {totalOffers}
                </h3>
              </div>
              <div className="d-none d-sm-flex align-items-center justify-content-center" style={{
                width: 'clamp(45px, 8vw, 60px)',
                height: 'clamp(45px, 8vw, 60px)',
                borderRadius: '16px',
                background: 'linear-gradient(135deg, #ff5722 0%, #f44336 100%)',
                fontSize: 'clamp(1.2rem, 3vw, 1.8rem)',
                color: 'white',
                boxShadow: '0 8px 20px rgba(0, 0, 0, 0.15)',
                animation: 'float 3s ease-in-out infinite'
              }}>
                <FontAwesomeIcon icon={faBolt} />
              </div>
            </div>
            <div className="d-flex align-items-center gap-2 position-relative" style={{
              fontSize: 'clamp(0.75rem, 1.5vw, 0.9rem)',
              color: 'var(--sand-700)',
              fontWeight: '600'
            }}>
              All limited offers
            </div>
          </div>
        </div>

        {/* Active Offers */}
        <div className="col-6 col-lg-3">
          <div className="h-100" style={{
            background: 'linear-gradient(135deg, rgba(40, 167, 69, 0.1) 0%, rgba(32, 201, 151, 0.1) 100%)',
            borderRadius: '16px',
            padding: 'clamp(1rem, 3vw, 1.8rem)',
            transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
            cursor: 'pointer',
            border: '1px solid rgba(255, 255, 255, 0.8)',
            boxShadow: '0 4px 15px rgba(0, 0, 0, 0.08)',
            position: 'relative',
            overflow: 'hidden'
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.transform = 'translateY(-8px) scale(1.02)';
            e.currentTarget.style.boxShadow = '0 12px 35px rgba(0, 0, 0, 0.15)';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.transform = 'translateY(0) scale(1)';
            e.currentTarget.style.boxShadow = '0 4px 15px rgba(0, 0, 0, 0.08)';
          }}>
            <div style={{
              position: 'absolute',
              top: '-20px',
              right: '-20px',
              width: '80px',
              height: '80px',
              borderRadius: '50%',
              background: 'linear-gradient(135deg, #28a745 0%, #20c997 100%)',
              opacity: 0.1,
              animation: 'pulse 3s ease-in-out infinite'
            }} />
            
            <div className="d-flex justify-content-between align-items-start mb-3 position-relative">
              <div className="flex-grow-1">
                <p className="mb-2 text-uppercase" style={{
                  fontSize: 'clamp(0.7rem, 1.5vw, 0.9rem)',
                  color: 'var(--sand-700)',
                  fontWeight: '600',
                  letterSpacing: '0.5px'
                }}>
                  Active
                </p>
                <h3 className="mb-0" style={{
                  fontSize: 'clamp(1.5rem, 4vw, 2.2rem)',
                  fontWeight: '800',
                  background: 'linear-gradient(135deg, #28a745 0%, #20c997 100%)',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  backgroundClip: 'text'
                }}>
                  {activeOffers}
                </h3>
              </div>
              <div className="d-none d-sm-flex align-items-center justify-content-center" style={{
                width: 'clamp(45px, 8vw, 60px)',
                height: 'clamp(45px, 8vw, 60px)',
                borderRadius: '16px',
                background: 'linear-gradient(135deg, #28a745 0%, #20c997 100%)',
                fontSize: 'clamp(1.2rem, 3vw, 1.8rem)',
                color: 'white',
                boxShadow: '0 8px 20px rgba(0, 0, 0, 0.15)',
                animation: 'float 3s ease-in-out infinite'
              }}>
                <FontAwesomeIcon icon={faFire} />
              </div>
            </div>
            <div className="d-flex align-items-center gap-2 position-relative" style={{
              fontSize: 'clamp(0.75rem, 1.5vw, 0.9rem)',
              color: 'var(--sand-700)',
              fontWeight: '600'
            }}>
              Currently running
            </div>
          </div>
        </div>

        {/* Expired Offers */}
        <div className="col-6 col-lg-3">
          <div className="h-100" style={{
            background: 'linear-gradient(135deg, rgba(220, 53, 69, 0.1) 0%, rgba(200, 35, 51, 0.1) 100%)',
            borderRadius: '16px',
            padding: 'clamp(1rem, 3vw, 1.8rem)',
            transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
            cursor: 'pointer',
            border: '1px solid rgba(255, 255, 255, 0.8)',
            boxShadow: '0 4px 15px rgba(0, 0, 0, 0.08)',
            position: 'relative',
            overflow: 'hidden'
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.transform = 'translateY(-8px) scale(1.02)';
            e.currentTarget.style.boxShadow = '0 12px 35px rgba(0, 0, 0, 0.15)';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.transform = 'translateY(0) scale(1)';
            e.currentTarget.style.boxShadow = '0 4px 15px rgba(0, 0, 0, 0.08)';
          }}>
            <div style={{
              position: 'absolute',
              top: '-20px',
              right: '-20px',
              width: '80px',
              height: '80px',
              borderRadius: '50%',
              background: 'linear-gradient(135deg, #dc3545 0%, #c82333 100%)',
              opacity: 0.1,
              animation: 'pulse 3s ease-in-out infinite'
            }} />
            
            <div className="d-flex justify-content-between align-items-start mb-3 position-relative">
              <div className="flex-grow-1">
                <p className="mb-2 text-uppercase" style={{
                  fontSize: 'clamp(0.7rem, 1.5vw, 0.9rem)',
                  color: 'var(--sand-700)',
                  fontWeight: '600',
                  letterSpacing: '0.5px'
                }}>
                  Expired
                </p>
                <h3 className="mb-0" style={{
                  fontSize: 'clamp(1.5rem, 4vw, 2.2rem)',
                  fontWeight: '800',
                  background: 'linear-gradient(135deg, #dc3545 0%, #c82333 100%)',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  backgroundClip: 'text'
                }}>
                  {expiredOffers}
                </h3>
              </div>
              <div className="d-none d-sm-flex align-items-center justify-content-center" style={{
                width: 'clamp(45px, 8vw, 60px)',
                height: 'clamp(45px, 8vw, 60px)',
                borderRadius: '16px',
                background: 'linear-gradient(135deg, #dc3545 0%, #c82333 100%)',
                fontSize: 'clamp(1.2rem, 3vw, 1.8rem)',
                color: 'white',
                boxShadow: '0 8px 20px rgba(0, 0, 0, 0.15)',
                animation: 'float 3s ease-in-out infinite'
              }}>
                ⏰
              </div>
            </div>
            <div className="d-flex align-items-center gap-2 position-relative" style={{
              fontSize: 'clamp(0.75rem, 1.5vw, 0.9rem)',
              color: 'var(--sand-700)',
              fontWeight: '600'
            }}>
              Past offers
            </div>
          </div>
        </div>

        {/* Scheduled Offers */}
        <div className="col-6 col-lg-3">
          <div className="h-100" style={{
            background: 'linear-gradient(135deg, rgba(255, 193, 7, 0.1) 0%, rgba(255, 152, 0, 0.1) 100%)',
            borderRadius: '16px',
            padding: 'clamp(1rem, 3vw, 1.8rem)',
            transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
            cursor: 'pointer',
            border: '1px solid rgba(255, 255, 255, 0.8)',
            boxShadow: '0 4px 15px rgba(0, 0, 0, 0.08)',
            position: 'relative',
            overflow: 'hidden'
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.transform = 'translateY(-8px) scale(1.02)';
            e.currentTarget.style.boxShadow = '0 12px 35px rgba(0, 0, 0, 0.15)';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.transform = 'translateY(0) scale(1)';
            e.currentTarget.style.boxShadow = '0 4px 15px rgba(0, 0, 0, 0.08)';
          }}>
            <div style={{
              position: 'absolute',
              top: '-20px',
              right: '-20px',
              width: '80px',
              height: '80px',
              borderRadius: '50%',
              background: 'linear-gradient(135deg, #ffc107 0%, #ff9800 100%)',
              opacity: 0.1,
              animation: 'pulse 3s ease-in-out infinite'
            }} />
            
            <div className="d-flex justify-content-between align-items-start mb-3 position-relative">
              <div className="flex-grow-1">
                <p className="mb-2 text-uppercase" style={{
                  fontSize: 'clamp(0.7rem, 1.5vw, 0.9rem)',
                  color: 'var(--sand-700)',
                  fontWeight: '600',
                  letterSpacing: '0.5px'
                }}>
                  Scheduled
                </p>
                <h3 className="mb-0" style={{
                  fontSize: 'clamp(1.5rem, 4vw, 2.2rem)',
                  fontWeight: '800',
                  background: 'linear-gradient(135deg, #ffc107 0%, #ff9800 100%)',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  backgroundClip: 'text'
                }}>
                  {scheduledOffers}
                </h3>
              </div>
              <div className="d-none d-sm-flex align-items-center justify-content-center" style={{
                width: 'clamp(45px, 8vw, 60px)',
                height: 'clamp(45px, 8vw, 60px)',
                borderRadius: '16px',
                background: 'linear-gradient(135deg, #ffc107 0%, #ff9800 100%)',
                fontSize: 'clamp(1.2rem, 3vw, 1.8rem)',
                color: 'white',
                boxShadow: '0 8px 20px rgba(0, 0, 0, 0.15)',
                animation: 'float 3s ease-in-out infinite'
              }}>
                📅
              </div>
            </div>
            <div className="d-flex align-items-center gap-2 position-relative" style={{
              fontSize: 'clamp(0.75rem, 1.5vw, 0.9rem)',
              color: 'var(--sand-700)',
              fontWeight: '600'
            }}>
              Upcoming offers
            </div>
          </div>
        </div>
      </div>

      {/* Main Content Card */}
      <div className="card border-0 shadow-sm" style={{
        backgroundColor: 'white',
        borderRadius: '12px',
        padding: '1.5rem'
      }}>
        {/* Filters Section */}
        <div className="mb-4 pb-4" style={{ borderBottom: '1px solid var(--sand-200)' }}>
          <div className="row g-3 align-items-end">
            {/* Search Bar */}
            <div className="col-12 col-lg-5">
              <label className="form-label mb-2" style={{ 
                color: 'var(--sand-800)', 
                fontSize: '0.875rem',
                fontWeight: '500'
              }}>
                <FontAwesomeIcon icon={faSearch} className="me-2" style={{ color: 'var(--sand-600)' }} />
                Search Offers
              </label>
              <input
                type="text"
                className="form-control"
                placeholder="Search by title or description..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                style={{
                  padding: '0.5rem 0.75rem',
                  borderRadius: '8px',
                  border: '1px solid var(--sand-300)',
                  fontSize: '0.875rem'
                }}
              />
            </div>

            {/* Status Filter */}
            <div className="col-12 col-md-6 col-lg-3">
              <label className="form-label mb-2" style={{ 
                color: 'var(--sand-800)', 
                fontSize: '0.875rem',
                fontWeight: '500'
              }}>
                <FontAwesomeIcon icon={faFilter} className="me-2" style={{ color: 'var(--sand-600)' }} />
                Filter
              </label>
              <Select
                options={statusOptions}
                value={selectedStatus}
                onChange={setSelectedStatus}
                placeholder="All Status"
                styles={customSelectStyles}
                isClearable
              />
            </div>

            {/* Add Offer Button */}
            <div className="col-12 col-md-6 col-lg-4">
              <button 
                className="btn w-100"
                onClick={() => setIsAddModalOpen(true)}
                style={{
                  padding: '0.5rem 1rem',
                  borderRadius: '6px',
                  backgroundColor: 'var(--sand-600)',
                  border: 'none',
                  color: 'white',
                  fontSize: '0.875rem',
                  fontWeight: '600',
                  transition: 'all 0.2s',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '0.5rem'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = 'var(--sand-700)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = 'var(--sand-600)';
                }}
              >
                <FontAwesomeIcon icon={faPlus} />
                Create New Offer
              </button>
            </div>
          </div>

          {/* Results Count */}
          <div className="row mt-3">
            <div className="col-12">
              <span style={{ color: 'var(--sand-700)', fontSize: '0.875rem', fontWeight: '500' }}>
                Showing {filteredOffers.length} of {totalOffers} offers
              </span>
            </div>
          </div>
        </div>

        {/* Offers Grid - 4 cards per row matching Sales */}
        <div className="row g-3">
          {filteredOffers.map((offer) => (
            <div key={offer.id} className="col-12 col-md-6 col-lg-3">
              <div className="card h-100 border-0" style={{
                borderRadius: '8px',
                border: '1px solid var(--sand-300)',
                transition: 'all 0.2s',
                overflow: 'hidden'
              }}>
                {/* Offer Header with Status */}
                <div style={{
                  position: 'relative',
                  paddingTop: '56.25%',
                  backgroundColor: 'var(--sand-100)',
                  background: `linear-gradient(135deg, ${getStatusColor(offer.status)}15 0%, ${getStatusColor(offer.status)}25 100%)`
                }}>
                  <div style={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '2rem'
                  }}>
                    ⚡
                  </div>
                  
                  {/* Status Badge */}
                  <span className="position-absolute top-0 end-0 m-2" style={{
                    padding: '0.25rem 0.5rem',
                    borderRadius: '4px',
                    backgroundColor: `${getStatusColor(offer.status)}15`,
                    color: getStatusColor(offer.status),
                    fontSize: '0.7rem',
                    fontWeight: '600',
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px'
                  }}>
                    {offer.status}
                  </span>

                  {/* Discount Badge */}
                  <div className="position-absolute top-0 start-0 m-2" style={{
                    backgroundColor: 'white',
                    borderRadius: '6px',
                    padding: '0.375rem 0.625rem',
                    boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)'
                  }}>
                    <div style={{
                      fontSize: '1rem',
                      fontWeight: '700',
                      color: '#ff5722',
                      lineHeight: '1'
                    }}>
                      {offer.discount}%
                    </div>
                    <div style={{
                      fontSize: '0.625rem',
                      color: '#6c757d',
                      fontWeight: '600',
                      textTransform: 'uppercase'
                    }}>
                      OFF
                    </div>
                  </div>
                </div>

                {/* Offer Info */}
                <div className="card-body p-3">
                  <h5 className="card-title mb-2" style={{
                    color: 'var(--sand-900)',
                    fontSize: '0.9375rem',
                    fontWeight: '600',
                    lineHeight: '1.3',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    display: '-webkit-box',
                    WebkitLineClamp: 2,
                    WebkitBoxOrient: 'vertical',
                    minHeight: '2.44rem'
                  }}>
                    {offer.title}
                  </h5>
                  
                  <p className="mb-2" style={{
                    color: 'var(--sand-700)',
                    fontSize: '0.8125rem',
                    lineHeight: '1.4',
                    display: '-webkit-box',
                    WebkitLineClamp: 2,
                    WebkitBoxOrient: 'vertical',
                    overflow: 'hidden'
                  }}>
                    {offer.description}
                  </p>

                  {/* Date Range */}
                  <div className="d-flex align-items-center gap-1 mb-2" style={{
                    fontSize: '0.75rem',
                    color: 'var(--sand-700)'
                  }}>
                    <FontAwesomeIcon icon={faCalendarAlt} style={{ fontSize: '0.7rem' }} />
                    <span>
                      {new Date(offer.startDate).toLocaleDateString('en-IN', { day: '2-digit', month: 'short' })} - {new Date(offer.endDate).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}
                    </span>
                  </div>

                  {/* Stock Info */}
                  {offer.stockLimit && (
                    <div className="mb-2 pb-2" style={{ borderBottom: '1px solid var(--sand-200)' }}>
                      <div className="d-flex justify-content-between mb-1" style={{
                        fontSize: '0.7rem',
                        color: 'var(--sand-700)'
                      }}>
                        <span>Stock</span>
                        <span style={{ fontWeight: '600' }}>
                          {offer.soldCount}/{offer.stockLimit}
                        </span>
                      </div>
                      <div style={{
                        height: '4px',
                        backgroundColor: 'var(--sand-200)',
                        borderRadius: '2px',
                        overflow: 'hidden'
                      }}>
                        <div style={{
                          height: '100%',
                          width: `${(offer.soldCount / offer.stockLimit) * 100}%`,
                          backgroundColor: offer.soldCount >= offer.stockLimit ? '#dc3545' : '#ff5722',
                          transition: 'width 0.3s'
                        }} />
                      </div>
                    </div>
                  )}

                  {/* Action Buttons */}
                  <div className="d-flex gap-2">
                    <button 
                      onClick={() => handleViewClick(offer)}
                      className="btn btn-sm flex-fill" style={{
                      backgroundColor: 'var(--sand-100)',
                      border: '1px solid var(--sand-300)',
                      color: 'var(--sand-800)',
                      padding: '0.375rem',
                      borderRadius: '4px',
                      fontSize: '0.75rem',
                      fontWeight: '600'
                    }}>
                      <FontAwesomeIcon icon={faEye} />
                    </button>
                    <button 
                      onClick={() => handleEditClick(offer)}
                      className="btn btn-sm flex-fill" style={{
                      backgroundColor: 'var(--sand-600)',
                      border: 'none',
                      color: 'white',
                      padding: '0.375rem',
                      borderRadius: '4px',
                      fontSize: '0.75rem',
                      fontWeight: '600'
                    }}>
                      <FontAwesomeIcon icon={faEdit} />
                    </button>
                    <button 
                      onClick={() => handleDeleteClick(offer)}
                      className="btn btn-sm" style={{
                      backgroundColor: '#dc354515',
                      border: '1px solid #dc354530',
                      color: '#dc3545',
                      padding: '0.375rem 0.5rem',
                      borderRadius: '4px',
                      fontSize: '0.75rem',
                      fontWeight: '600'
                    }}>
                      <FontAwesomeIcon icon={faTrash} />
                    </button>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* No Results */}
        {filteredOffers.length === 0 && (
          <div className="text-center py-5">
            <div className="mb-3" style={{ fontSize: '3rem', opacity: 0.3 }}>⚡</div>
            <h5 className="mb-2" style={{ color: 'var(--sand-800)', fontWeight: '600' }}>
              No offers found
            </h5>
            <p className="mb-3" style={{ color: 'var(--sand-700)', fontSize: '0.875rem' }}>
              {searchQuery || selectedStatus ? 'Try adjusting your filters' : 'Create your first limited offer'}
            </p>
            {!searchQuery && !selectedStatus && (
              <button 
                className="btn"
                onClick={() => setIsAddModalOpen(true)}
                style={{
                  padding: '0.5rem 1.5rem',
                  borderRadius: '6px',
                  backgroundColor: 'var(--sand-600)',
                  border: 'none',
                  color: 'white',
                  fontSize: '0.875rem',
                  fontWeight: '600'
                }}
              >
                <FontAwesomeIcon icon={faPlus} className="me-2" />
                Create New Offer
              </button>
            )}
          </div>
        )}
      </div>

      {/* Modals */}
      <AddLimitedOfferModal 
        isOpen={isAddModalOpen} 
        onClose={() => setIsAddModalOpen(false)} 
      />
      <ViewLimitedOfferModal 
        isOpen={viewModal.isOpen} 
        onClose={() => setViewModal({ isOpen: false, offer: null })} 
        offer={viewModal.offer}
        onEdit={(offer) => {
          setViewModal({ isOpen: false, offer: null });
          setEditModal({ isOpen: true, offer });
        }}
      />
      <EditLimitedOfferModal 
        isOpen={editModal.isOpen} 
        onClose={() => setEditModal({ isOpen: false, offer: null })} 
        offer={editModal.offer}
      />
      <DeleteConfirmationModal
        isOpen={deleteModal.isOpen}
        onClose={() => setDeleteModal({ isOpen: false, offer: null })}
        onConfirm={handleDeleteConfirm}
        itemName={deleteModal.offer?.title}
        isDeleting={isDeleting}
      />
    </div>
  );
}
